import { CartLinesUpdateEvent } from '@theme/standard-events';

/**
 * Central cart section renderer.
 *
 * Listens for the Shopify standard cart events (shopify:cart:lines-update,
 * shopify:cart:note-update, shopify:cart:discount-update) on document — these are
 * dispatched by the theme's own cart mutations AND auto-emitted by configured
 * Shopify.actions.updateCart calls from apps. After the mutation settles it fetches
 * all cart sections in ONE Section Rendering API GET and swaps every marked region.
 *
 * Marker contract (attributes live on the exact element whose innerHTML is swapped):
 *   data-cart-section="<key>"        unique key, matches the live marker to the same
 *                                    marker in the fetched section HTML
 *   data-cart-section-id="<id>"      Section Rendering API id ({{ section.id }} or a
 *                                    static section name like 'theme-cart-icon-bubble')
 *
 * Only innerHTML is ever swapped: the fetched icon-bubble section renders without
 * section context (an <a>), while the live DOM can be a <button command> — the wrapper
 * element must never be replaced.
 *
 * After every render a bubbling 'cart-sections:rendered' CustomEvent fires on document
 * with detail { cart, ok } so components can write error text / restore focus into the
 * freshly swapped DOM.
 */
class CartSectionRenderer {
  #renderToken = 0;
  #pendingCount = 0;

  constructor() {
    ['shopify:cart:lines-update', 'shopify:cart:note-update', 'shopify:cart:discount-update'].forEach((type) => {
      document.addEventListener(type, this.#onCartMutation);
    });
  }

  #onCartMutation = async (event) => {
    this.#startLoading();

    let cart = null;
    let ok = true;

    if (event.promise) {
      try {
        ({ cart } = await event.promise);
      }
      catch {
        // The mutation failed, but the cart may still have changed (e.g. a 422 where
        // part of the quantity was added) — re-sync the actual state. Fetched via the
        // AJAX API and converted, because the Shopify.actions.getCart() cart shape does
        // not pass the standard-events payload validation.
        try {
          const ajaxCart = await fetch(`${routes.cart_url}.js`).then(response => response.json());
          cart = CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart);
        }
        catch {
          ok = false;
        }
      }
    }

    if (ok) {
      try {
        await this.render(cart);
      }
      catch (error) {
        debug() && console.error('Cart section render failed', error);
        ok = false;
      }
    }

    this.#stopLoading();

    document.dispatchEvent(
      new CustomEvent('cart-sections:rendered', {
        bubbles: true,
        detail: { cart,
          ok },
      }),
    );
  };

  /**
   * Re-renders every live [data-cart-section] marker from one Section Rendering API GET.
   * Public so components can force a re-sync: window.cartSectionRenderer.render()
   */
  async render(cart = null) {
    const token = ++this.#renderToken;
    const markers = [...document.querySelectorAll('[data-cart-section]')];
    if (markers.length === 0) return;

    const sectionIds = [...new Set(markers.map(marker => marker.dataset.cartSectionId).filter(Boolean))];
    const response = await fetch(`${window.location.pathname}?sections=${sectionIds.join(',')}`);
    if (!response.ok) throw new Error(`Sections request failed (${response.status})`);
    const sections = await response.json();

    // A newer mutation superseded this render; its own render will reflect the final state
    if (token !== this.#renderToken) return;

    const fragments = new Map();
    markers.forEach((marker) => {
      const sectionId = marker.dataset.cartSectionId;
      if (!sections[sectionId]) {
        debug() && console.warn(`Cart section '${sectionId}' missing in sections response`);
        return;
      }

      if (!fragments.has(sectionId)) {
        fragments.set(sectionId, document.createRange().createContextualFragment(sections[sectionId]));
      }

      const source = fragments.get(sectionId).querySelector(`[data-cart-section="${marker.dataset.cartSection}"]`);
      if (source) {
        marker.innerHTML = source.innerHTML;
      }
      else {
        marker.innerHTML = '';
        debug() && console.warn(`Cart section marker '${marker.dataset.cartSection}' missing in fetched HTML of '${sectionId}'`);
      }

      window.initDialogs?.(marker);
    });

    const isEmpty = cart ? cart.totalQuantity === 0 : !document.querySelector('[data-cart-section="items"] [data-cart-item]');
    document.querySelectorAll('cart-items').forEach(element => element.classList.toggle('cart-is-empty', isEmpty));
  }

  #startLoading() {
    this.#pendingCount++;
    document.querySelectorAll('cart-items').forEach(element => element.classList.add('cart-items-is-loading'));
    document.querySelector('[data-cart-loader-active="show"]')?.classList.remove('hidden');
    document.querySelector('[data-cart-loader-active="hidden"]')?.classList.add('hidden');
    document.querySelectorAll('[data-shopping-cart-line-item-status]').forEach(element => element.setAttribute('aria-hidden', 'false'));
  }

  #stopLoading() {
    if (--this.#pendingCount > 0) return;
    this.#pendingCount = 0;

    document.querySelectorAll('cart-items').forEach(element => element.classList.remove('cart-items-is-loading'));
    document.querySelector('[data-cart-loader-active="show"]')?.classList.add('hidden');
    document.querySelector('[data-cart-loader-active="hidden"]')?.classList.remove('hidden');
    document.querySelectorAll('[data-shopping-cart-line-item-status]').forEach(element => element.setAttribute('aria-hidden', 'true'));
  }
}

const cartSectionRenderer = new CartSectionRenderer();
window.cartSectionRenderer = cartSectionRenderer;

export default cartSectionRenderer;
